import React from 'react'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import Spinner from 'react-bootstrap/Spinner'
import { ReactComponent as ExportIcon } from 'images/performance-report/export.svg'
import { useCSVDownloader } from 'react-papaparse'
import classNames from 'classnames/bind'
import styles from './ExportButton.module.scss'
import { convertCamelCasetoTitleCase } from 'utils/helpers'
import PropTypes from 'prop-types'
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone'
import { captureReportExported } from 'config/posthog/events'
import { ReportNames } from 'config/posthog/constants'

const cx = classNames.bind(styles)

// The list of different possible event types sent by the backend
const eventTypeList = ['price_change_requested', 'price_change_detected', 'price_change_reported_to_cma']

export const handleExportCSV = (data) => {
  if (!data?.length) return []

  const formattedData = data.map(item => {
    const {
      id,
      station,
      fuelGrade,
      oldPrice,
      newPrice,
      status,
      events
    } = item

    // Get specific events needed for "Requested By" and "Cma Reference" columns
    const requestedEvent = events.find(event => event.type === "price_change_requested")
    const reportedEvent = events.find(event => event.type === "price_change_reported_to_cma")

    // Base row
    const row = {
      "ID": id,
      "Station Name": station?.name || '',
      "Brand": station?.brand?.name || '',
      "Fuel Grade": fuelGrade?.name || '',
      "Status": convertCamelCasetoTitleCase(status),
      "Old Price": oldPrice,
      "New Price": newPrice,
      "Price Change": (Number(newPrice) - Number(oldPrice)).toFixed(2),
      "Requested By": requestedEvent?.user?.name || '',
    }

    // Map each event type to timestamp or leave blank if event hasn't occured
    eventTypeList.forEach(type => {
      const matchingEvent = events.find(event => event.type === type)

      let colName = convertCamelCasetoTitleCase(type)

      if(colName === 'Price Change Requested') colName = 'Price Change At'

      row[colName] = matchingEvent?.eventAt || ''
    })

    row["Cma Reference"] = reportedEvent?.cmaResponse?.reference || ''

    return row
  })

  return formattedData
}

const ExportButton = ({ data, loading, period }) => {
  const { t } = useTranslation()
  const { CSVDownloader, Type } = useCSVDownloader()
  const { companyTimeZone } = useCompanyTimeZone()

  const handleExportCSVWrapper = () => handleExportCSV(data)

  const handleClick = () => {
    captureReportExported(ReportNames.POLE_PRICE_AUDIT, companyTimeZone, period)
  }

  return (
    <div className={cx('export-button', { loading })} data-testid="export-button">
      <CSVDownloader
        type={Type.Button}
        filename={t('widgets.polePriceAudit.csvFilename')}
        bom
        data={handleExportCSVWrapper}
      >
        <div data-testid="wrapper" onClick={handleClick}>
          {loading ? (
            <Spinner animation="border" variant="light" data-testid="loading" />
          ) : (
            <>
              <ExportIcon data-testid="export-icon" />
              {t('widgets.performanceReport.exportToCSV')}
            </>
          )}
        </div>
      </CSVDownloader>
    </div>
  )
}

ExportButton.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

      station: PropTypes.shape({
        name: PropTypes.string.isRequired,
        brand: PropTypes.shape({
          name: PropTypes.string
        })
      }),

      fuelGrade: PropTypes.shape({
        name: PropTypes.string.isRequired
      }),

      oldPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      newPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

      status: PropTypes.string.isRequired,

      events: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          eventAt: PropTypes.string,
          user: PropTypes.shape({
            name: PropTypes.string
          }),
          cmaResponse: PropTypes.shape({
            reference: PropTypes.string
          })
        })
      )
    })
  ),
  loading: PropTypes.bool,
  period: PropTypes.shape({
    from: PropTypes.instanceOf(moment),
    to: PropTypes.instanceOf(moment),
  }),
}

ExportButton.defaultProps = {
  data: [],
  loading: true,
  period: null,
}

export { ExportButton }
